export enum CommandKind {
  PickCard = 0, PlaceSegment = 1, PlaceTower = 2, SkipOffer = 3, Reroll = 4,
  StartWaves = 5, CallNextStage = 6, UpgradeTower = 7, SellTower = 8, SetPriority = 9, UnpickCard = 10, SetRally = 11,
  /** Tier 4 (M21): spend the picked Ascend card on branch `a` (0 or 1); spend the picked skill card. */
  ChooseBranch = 12, LearnSkill = 13,
  /** M40 (sim/wyrm.ts): with the Wyrm's choice open, another try (the app has charged for it) or let it go. */
  WyrmRetry = 14, WyrmRelease = 15,
  /** Pets v2 (sim/wildPet.ts): the wild pet on the grass was tapped: it comes down the road as a boss. */
  EngagePet = 16,
  /** M55: spend the picked Move card: tower `a` moves to cell (`b`, `c`). */
  MoveTower = 17,
}

export enum TargetPriority { First = 0, Last = 1, Strongest = 2, Weakest = 3 }

/** A player input. Every mutation of the sim goes through one of these so runs can be replayed. */
export interface Command { readonly kind: CommandKind; readonly a: number; readonly b: number; readonly c: number }

export interface TimedCommand { readonly tick: number; readonly command: Command }

const mk = (kind: CommandKind, a = 0, b = 0, c = 0): Command => ({ kind, a, b, c });

export const Commands = {
  pickCard: (index: number) => mk(CommandKind.PickCard, index),
  unpickCard: () => mk(CommandKind.UnpickCard),
  placeSegment: (ghostIndex: number) => mk(CommandKind.PlaceSegment, ghostIndex),
  placeTower: (x: number, y: number) => mk(CommandKind.PlaceTower, x, y),
  skipOffer: () => mk(CommandKind.SkipOffer),
  reroll: () => mk(CommandKind.Reroll),
  startWaves: () => mk(CommandKind.StartWaves),
  callNextStage: () => mk(CommandKind.CallNextStage),
  /** Applies the picked Upgrade card to a tower. */
  upgradeTower: (towerId: number) => mk(CommandKind.UpgradeTower, towerId),
  sellTower: (towerId: number) => mk(CommandKind.SellTower, towerId),
  setPriority: (towerId: number, p: TargetPriority) => mk(CommandKind.SetPriority, towerId, p),
  /** Moves the rally flag; every troop re-routes to it. */
  setRally: (x: number, y: number) => mk(CommandKind.SetRally, x, y),
  /** Spends the picked Ascend card: the type takes its branch `slot` (0 = the first in branches.json, 1 = the second). */
  chooseBranch: (slot: number) => mk(CommandKind.ChooseBranch, slot),
  /** Spends the picked skill card. */
  learnSkill: () => mk(CommandKind.LearnSkill),
  /** The Starfall Wyrm flew off (M40): it comes again from the portal, wounded (`retryKeepsDamage`: the HP it fled with) or at full HP. The app charges `retryGems` first. */
  wyrmRetry: () => mk(CommandKind.WyrmRetry),
  /** The Starfall Wyrm flew off (M40): let it go; the run carries on. */
  wyrmRelease: () => mk(CommandKind.WyrmRelease),
  /** Pets v2: chase the wild pet (valid only while it is on the grass and the action runs; sim/wildPet.ts canEngagePet). */
  engagePet: () => mk(CommandKind.EngagePet),
  /** Spends the picked Move card: the tower goes to (x, y), a legal empty square, keeping its tier, branch and kills. */
  moveTower: (towerId: number, x: number, y: number) => mk(CommandKind.MoveTower, towerId, x, y),
};

export function commandToString(c: Command): string { return `${CommandKind[c.kind]}(${c.a},${c.b},${c.c})`; }

export enum CommandResult {
  Ok = 0, WrongPhase = 1, NoOffer = 2, InvalidIndex = 3, NothingPicked = 4, AlreadyPicked = 5, WrongCardKind = 6,
  InvalidPlacement = 7, NoRerollCharges = 8, MaxTier = 10, NoSuchTower = 11, RoadLocked = 12, LevelOver = 13, CannotSell = 14,
  /** M40: a Wyrm command with no Wyrm choice open. */
  NoWyrmChoice = 15,
  /** Pets v2: EngagePet with no wild pet to chase now (none, not yet, gone, already engaged, or the action is paused). */
  NoWildPet = 16,
}
